import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { FC, useMemo } from 'react';
import { Counter } from 'src/components/Counter';
import { useWeightInput } from 'src/hooks/useWeightInput';
import { Order, Cart, trpc, OrderContainer } from 'src/trpc';
import { MoneyFromCents, safeMoneyFromCents } from '@libs/services/money';
import { NumericFormat } from 'react-number-format';

interface FixedPricingState {
  weight: number;
  additionalMaterials: { [materialId: string]: number };
  usageByContainer: { [containerId: string]: number };
}

interface Props {
  order: Order;
  containers: OrderContainer[];
  cart: Cart;
  state: FixedPricingState;
  updateState: (updates: Partial<FixedPricingState>) => void;
}

export const FixedPricingForm: FC<Props> = ({ order, containers, cart, state, updateState }) => {
  const { weightInput, setWeightInput, commitWeight } = useWeightInput(state.weight);

  const { data: materials } = trpc.admin.material.getMaterials.useQuery(undefined, {
    staleTime: Infinity,
    gcTime: Infinity,
    suspense: false,
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // Extract fixed pricing details from quotedPricingDetails
  const fixedDetails = useMemo(() => {
    const containerPricing = order.quotedPricingDetails?.quote?.pricing?.containers?.[0];
    const materialPricing = order.quotedPricingDetails?.quote?.pricing?.materials;

    return {
      includedDays: containerPricing?.includedDays || 0,
      dailyOverageFeeInCents: containerPricing?.dailyOverageFeeInCents || 0,
      perTonRateInCents: materialPricing?.perTonRateInCents || order.pricePerTonInCents || 0,
    };
  }, [order.quotedPricingDetails, order.pricePerTonInCents]);

  // Calculate weight cost (all weight in fixed pricing incurs cost)
  const weightCost = state.weight * fixedDetails.perTonRateInCents;

  // Calculate container overage for each container
  const containerOverages = useMemo(() => {
    // Extract extra days paid at checkout from cart
    const totalDaysPaidAtCheckout = cart.lineItems?.containers?.[0]?.totalDays || 0;
    const extraDaysPaidAtCheckout = Math.max(
      0,
      totalDaysPaidAtCheckout - fixedDetails.includedDays,
    );

    return containers.map((container) => {
      const usedDays = state.usageByContainer[container._id] || 0;

      // Calculate days beyond included
      const daysOver = Math.max(0, usedDays - fixedDetails.includedDays);

      // Calculate refund or additional charge (mutually exclusive)
      const refundDays = Math.max(0, extraDaysPaidAtCheckout - daysOver);
      const additionalDays = Math.max(0, daysOver - extraDaysPaidAtCheckout);

      const refundAmount = refundDays * fixedDetails.dailyOverageFeeInCents;
      const additionalCost = additionalDays * fixedDetails.dailyOverageFeeInCents;

      return {
        containerId: container._id,
        usedDays,
        additionalDays,
        additionalCost,
        refundDays,
        refundAmount,
      };
    });
  }, [
    containers,
    state.usageByContainer,
    fixedDetails.includedDays,
    fixedDetails.dailyOverageFeeInCents,
    cart.lineItems?.containers,
  ]);

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Fixed Weight Section */}
      <div className="flex flex-col gap-2 w-full">
        <label className="text-sm text-muted">
          Total Weight (tons) - Weight not included in package
        </label>
        <div className="flex flex-row gap-2 text-sm text-muted">
          <NumericFormat
            step={0.1}
            thousandSeparator={true}
            type="text"
            inputMode="decimal"
            className="w-full"
            value={weightInput}
            onValueChange={(values) => setWeightInput(values.value)}
            onBlur={() => commitWeight(updateState)}
            customInput={Input}
            placeholder="Enter weight"
            valueIsNumericString={true}
            decimalScale={2}
            allowNegative={false}
            name="weight"
          />
          <div className="flex flex-row gap-2 items-center">
            <>
              <span className="line-through">
                {MoneyFromCents(cart.lineItems?.materials || 0).toFormat()}
              </span>
              <Badge className="text-xs" variant="success">
                Paid
              </Badge>
            </>

            <div className="min-w-[100px] text-right">
              <span>{state.weight > 0 ? safeMoneyFromCents(weightCost).toFormat() : '$0.00'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Container Days Section */}
      <div className="flex flex-col gap-2 w-full text-sm text-muted">
        <label className="text-sm text-muted">
          Container Service Days - {fixedDetails.includedDays} days included
        </label>
        {containers.map((c) => {
          const overage = containerOverages.find((co) => co.containerId === c._id);
          return (
            <div key={c._id} className="flex flex-col gap-2">
              <div className="flex flex-row w-full items-center gap-4">
                <label className="whitespace-nowrap">
                  {c.friendlyId} ({c.containerTypeInfo.size})
                </label>
                <NumericFormat
                  thousandSeparator={true}
                  type="text"
                  inputMode="numeric"
                  step={1}
                  className="w-full"
                  min={0}
                  value={state.usageByContainer[c._id] || 0}
                  onValueChange={(values) =>
                    updateState({
                      usageByContainer: {
                        ...state.usageByContainer,
                        [c._id]: parseInt(values.value) || 0,
                      },
                    })
                  }
                  customInput={Input}
                  placeholder="0"
                  valueIsNumericString={true}
                  decimalScale={0}
                  name={c._id}
                />
                <span className="min-w-[100px] text-right">
                  {overage && overage.refundDays > 0
                    ? `- ${safeMoneyFromCents(overage.refundAmount).toFormat()}`
                    : overage && overage.additionalDays > 0
                      ? safeMoneyFromCents(overage.additionalCost).toFormat()
                      : '$0.00'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="h-[1px] w-full bg-disabled"></div>

      {/* Additional Materials Section */}
      <h4 className="font-semibold text-base">Additional Materials</h4>
      <div className="flex flex-col gap-2.5 max-h-96 overflow-y-auto">
        {!materials && <div className="text-sm text-muted">Loading materials...</div>}
        {materials && materials.length === 0 && (
          <div className="text-sm text-muted">No materials available</div>
        )}
        {materials && materials.filter((m) => m.extraChargeInCents > 0).length === 0 && (
          <div className="text-sm text-muted">No materials with additional charges</div>
        )}
        {materials
          ?.sort((a, b) => a.name.localeCompare(b.name))
          ?.filter((m) => m.extraChargeInCents > 0)
          .map((material) => (
            <div
              key={material._id}
              className="flex flex-row gap-2 w-full items-center text-muted font-medium text-base"
            >
              <span className="flex-1">{material.name}</span>
              <div className="flex-1 flex flex-row gap-4 items-center">
                <Counter
                  onChange={(e) =>
                    updateState({
                      additionalMaterials: {
                        ...state.additionalMaterials,
                        [material._id]: e,
                      },
                    })
                  }
                  value={state.additionalMaterials[material._id] || 0}
                />
              </div>
              <p className="min-w-16">
                {MoneyFromCents(
                  (state.additionalMaterials[material._id] || 0) * material.extraChargeInCents,
                ).toFormat()}
              </p>
            </div>
          ))}
      </div>
    </div>
  );
};
